import React, { useState } from "react";
import { Button, DatePicker, Modal, Popover, Space, Tag, message } from "antd";
import { DownloadOutlined, ExclamationCircleFilled } from "@ant-design/icons";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { UploadRowBucket } from "../../../../components/upload/shared/ExcelUploadStageModal";

// Shared pieces of the three Daily Execution uploads (Civil, Material-Stores,
// Mechanical): the status cell, number cells, preview tabs, workbook writer
// and the "filled day sheet" download control.

export const rowErrors = (record: any): string[] =>
  Array.isArray(record?.Error) ? record.Error : record?.Error ? [record.Error] : [];

export const statusColumn = {
  title: "Status",
  key: "rowStatus",
  width: 96,
  fixed: "left" as const,
  render: (_: any, record: any) => {
    const errors = rowErrors(record);
    if (errors.length) {
      return (
        <Popover
          placement="right"
          title={<span style={{ color: "#e85347" }}>Row errors</span>}
          content={
            <div style={{ maxWidth: 360 }}>
              {errors.map((err, i) => (
                <div key={i} style={{ marginBottom: 4 }}>
                  • {err}
                </div>
              ))}
            </div>
          }
        >
          <Tag color="red">Error</Tag>
        </Popover>
      );
    }
    if (record?.status === "Applied") return <Tag color="blue">Saved</Tag>;
    if (record?.status === "Warning") return <Tag color="gold">Warning</Tag>;
    return <Tag color="green">Valid</Tag>;
  },
};

export const rowColumn = { title: "Row", dataIndex: "Row", key: "Row", width: 60, fixed: "left" as const };

export const numberColumn = (title: string, dataIndex: string, width = 110) => ({
  title,
  dataIndex,
  key: dataIndex,
  width,
  align: "right" as const,
  render: (value: any) =>
    value === "" || value === undefined || value === null ? (
      <span style={{ color: "#8094ae" }}>—</span>
    ) : (
      <span className="tabular-nums">{value}</span>
    ),
});

export const moneyColumn = (title: string, dataIndex: string, width = 120) => ({
  title,
  dataIndex,
  key: dataIndex,
  width,
  align: "right" as const,
  render: (value: any) =>
    value === "" || value === undefined || value === null ? (
      <span style={{ color: "#8094ae" }}>—</span>
    ) : (
      <span className="tabular-nums">
        ₹{Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
    ),
});

export const messageColumns = [
  {
    title: "Warning",
    dataIndex: "Warning",
    key: "Warning",
    width: 320,
    render: (value: any) =>
      value ? <span style={{ color: "#b58a1f", fontSize: 12 }}>{value}</span> : null,
  },
  {
    title: "Error details",
    key: "errorDetails",
    width: 320,
    render: (_: any, record: any) => {
      const errors = rowErrors(record);
      return errors.length ? (
        <span style={{ color: "#e85347", fontSize: 12 }}>{errors.join("; ")}</span>
      ) : null;
    },
  },
];

export const bucketRows = (rows: any[]): UploadRowBucket[] => {
  const errors = rows.filter((row) => rowErrors(row).length);
  const warnings = rows.filter((row) => !rowErrors(row).length && row?.status === "Warning");
  return [
    { key: "all", label: `All (${rows.length})`, rows },
    { key: "valid", label: `Will save (${rows.length - errors.length})`, rows: rows.filter((row) => !rowErrors(row).length) },
    { key: "warnings", label: `Warnings (${warnings.length})`, rows: warnings },
    { key: "errors", label: `Errors (${errors.length})`, rows: errors },
  ];
};

export const reportStatus = (row: any) =>
  rowErrors(row).length ? "Error" : row.status === "Applied" ? "Saved" : row.status === "Warning" ? "Warning" : "Valid";

export const reportMessageColumns = [
  { header: "Warning", width: 60, value: (row: any) => row.Warning || "" },
  { header: "Error details", width: 60, value: (row: any) => rowErrors(row).join("; ") },
];

/** Dates are written as TEXT (15-Sep-2026): a date cell would shift a day across time zones. */
export const sheetDate = (iso: string) => (iso ? dayjs(iso).format("DD-MMM-YYYY") : "");

export interface SheetSpec {
  name: string;
  headers: string[];
  rows: any[][];
  widths: number[];
}

export const writeWorkbook = (fileName: string, sheets: SheetSpec[], instructions: string[][]) => {
  const workbook = XLSX.utils.book_new();
  sheets.forEach((sheet) => {
    const ws = XLSX.utils.aoa_to_sheet([sheet.headers, ...sheet.rows]);
    ws["!cols"] = sheet.widths.map((wch) => ({ wch }));
    XLSX.utils.book_append_sheet(workbook, ws, sheet.name);
  });
  const help = XLSX.utils.aoa_to_sheet(instructions);
  help["!cols"] = [{ wch: 24 }, { wch: 120 }];
  XLSX.utils.book_append_sheet(workbook, help, "Instructions");
  XLSX.writeFile(workbook, fileName);
};

/** Runs `work` over `items` a few at a time — enough to be quick, few enough to be polite. */
export const inBatches = async <T, R>(items: T[], size: number, work: (item: T) => Promise<R>): Promise<R[]> => {
  const out: R[] = [];
  for (let i = 0; i < items.length; i += size) {
    // eslint-disable-next-line no-await-in-loop
    out.push(...(await Promise.all(items.slice(i, i + size).map(work))));
  }
  return out;
};

/**
 * A refusal to build the sheet that the USER has to act on — the day is missing
 * something no spreadsheet can supply. Shown as a modal that must be dismissed,
 * not a toast that scrolls away: there is nothing to download and a next step to
 * take. Anything else thrown by `build` is a failure, and stays a toast.
 */
export class DownloadBlocked extends Error {
  title: string;
  nextStep?: string;
  /**
   * Key of the one thing the user can do about it, when there IS one — the
   * caller maps it to a button. Left unset when the fix is somewhere this
   * screen cannot send them.
   */
  action?: string;

  constructor(message: string, title: string, nextStep?: string, action?: string) {
    super(message);
    // Transpiled `extends Error` can break instanceof, so the NAME is the check.
    this.name = "DownloadBlocked";
    this.title = title;
    this.nextStep = nextStep;
    this.action = action;
  }
}

export interface BlockedAction {
  label: string;
  run: (date: string) => void;
}

/**
 * "Download the day's sheet, filled": pick a date, get a workbook of what the
 * day already holds, change it, upload it back.
 */
export const FilledSheetDownload: React.FC<{
  label: string;
  hint: React.ReactNode;
  initialDate?: string;
  build: (date: string) => Promise<number>;
  // What a DownloadBlocked's `action` key resolves to — the button the blocking
  // modal offers. Routing belongs to the page, not to this control.
  actions?: Record<string, BlockedAction>;
}> = ({ label, hint, initialDate, build, actions }) => {
  const [date, setDate] = useState(initialDate || dayjs().format("YYYY-MM-DD"));
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const count = await build(date);
      if (count === 0) message.info(`Nothing to fill for ${sheetDate(date)} — the template has headers only`);
    } catch (err: any) {
      if (err?.name === "DownloadBlocked") {
        const action = err.action ? actions?.[err.action] : undefined;
        const content = (
          <div>
            <p style={{ marginBottom: err.nextStep ? 8 : 0 }}>{err.message}</p>
            {err.nextStep && <p style={{ color: "#526484", marginBottom: 0 }}>{err.nextStep}</p>}
          </div>
        );
        // With somewhere to send them, the modal's primary button goes there;
        // without, it is an acknowledgement.
        if (action) {
          Modal.confirm({
            title: err.title,
            width: 520,
            icon: <ExclamationCircleFilled style={{ color: "#faad14" }} />,
            content,
            okText: action.label,
            cancelText: "Close",
            onOk: () => action.run(date),
          });
        } else {
          Modal.warning({ title: err.title, width: 520, okText: "Got it", content });
        }
        return;
      }
      message.error(err?.message || "Could not build the filled sheet");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="pms-exec-upload-filled">
      <div className="pms-exec-upload-filled-head">{label}</div>
      <div className="pms-exec-upload-filled-hint">{hint}</div>
      <Space wrap>
        <DatePicker
          value={dayjs(date)}
          onChange={(value) => value && setDate(value.format("YYYY-MM-DD"))}
          allowClear={false}
          format="DD-MMM-YYYY"
          disabledDate={(d) => d.isAfter(dayjs(), "day")}
        />
        <Button icon={<DownloadOutlined />} loading={busy} onClick={run}>
          Download filled sheet
        </Button>
      </Space>
    </div>
  );
};
